/**
 * In-memory store backing the mock calibration-runs API.
 *
 * Single module-level store so every mock-API call shares state within a
 * browser tab. Runs/metrics support the mutations the UI actually performs
 * (create/delete/cancel a run, create/update/delete a metric) so those flows
 * are exercisable without a backend, not just the read paths.
 */

import type { AISUser } from '@frontend-platform/user-directory';
import type { BotEnvironment } from '../api/environments-api';
import type { RunDetail } from '../components/detail/types';
import type { CalibrationMetric, CalibrationRun } from '../lib/types';

const MOCK_USERS: AISUser[] = [
  { id: 'mock-user-1', name: 'Asha Kapoor', email: 'asha.kapoor@example.com' },
  { id: 'mock-user-2', name: 'Diego Fernandez', email: 'diego.fernandez@example.com' },
  { id: 'mock-user-3', name: 'Priya Nair', email: 'priya.nair@example.com' },
];

function user(index: number): AISUser {
  return MOCK_USERS[index % MOCK_USERS.length] as AISUser;
}

function isoDaysAgo(days: number, hours = 0): string {
  const ms = days * 24 * 60 * 60 * 1000 + hours * 60 * 60 * 1000;
  return new Date(1_735_689_600_000 - ms).toISOString();
}

let nextRunSeq = 100;
let nextMetricSeq = 100;

function seedRuns(): CalibrationRun[] {
  return [
    {
      id: 1,
      botId: 'mock-bot',
      botReferenceId: 'mock-bot-ref',
      runId: 'run-completed-1',
      runName: 'Weekly quality sweep',
      env: 'SANDBOX',
      status: 'COMPLETED',
      startTime: isoDaysAgo(3),
      endTime: isoDaysAgo(3, -1),
      createdAt: isoDaysAgo(3),
      updatedAt: isoDaysAgo(3, -1),
      createdBy: user(0),
      updatedBy: user(0),
      isActive: true,
      isDeleted: false,
      evaluationEnvironment: 'SANDBOX',
      historicalEnvironment: 'SANDBOX',
      selectedConversationCount: '482',
      recommendationCount: 3,
      channel: 'web',
    },
    {
      id: 2,
      botId: 'mock-bot',
      botReferenceId: 'mock-bot-ref',
      runId: 'run-completed-2',
      runName: 'Live sampling — launch day',
      env: 'LIVE',
      status: 'COMPLETED',
      startTime: isoDaysAgo(7),
      endTime: isoDaysAgo(7, -2),
      createdAt: isoDaysAgo(7),
      updatedAt: isoDaysAgo(7, -2),
      createdBy: user(1),
      updatedBy: user(1),
      isActive: true,
      isDeleted: false,
      evaluationEnvironment: 'LIVE',
      historicalEnvironment: 'LIVE',
      selectedConversationCount: '1000',
      recommendationCount: 1,
      channel: 'whatsapp',
    },
    {
      id: 3,
      botId: 'mock-bot',
      botReferenceId: 'mock-bot-ref',
      runId: 'run-in-progress-1',
      runName: 'Post-release regression check',
      env: 'SANDBOX',
      status: 'IN_PROGRESS',
      startTime: isoDaysAgo(0, 1),
      createdAt: isoDaysAgo(0, 1),
      updatedAt: isoDaysAgo(0, 1),
      createdBy: user(2),
      updatedBy: user(2),
      isActive: true,
      isDeleted: false,
      evaluationEnvironment: 'SANDBOX',
      historicalEnvironment: 'SANDBOX',
      selectedConversationCount: '250',
      recommendationCount: 0,
      channel: 'web',
    },
    {
      id: 4,
      botId: 'mock-bot',
      botReferenceId: 'mock-bot-ref',
      runId: 'run-scheduled-1',
      runName: 'Nightly scheduled run',
      env: 'SANDBOX',
      status: 'INITIATED',
      createdAt: isoDaysAgo(0),
      updatedAt: isoDaysAgo(0),
      createdBy: user(0),
      updatedBy: user(0),
      isActive: true,
      isDeleted: false,
      evaluationEnvironment: 'SANDBOX',
      historicalEnvironment: 'SANDBOX',
      selectedConversationCount: '300',
      recommendationCount: 0,
      channel: 'web',
    },
    {
      id: 5,
      botId: 'mock-bot',
      botReferenceId: 'mock-bot-ref',
      runId: 'run-failed-1',
      runName: 'Backfill — Q3 conversations',
      env: 'SANDBOX',
      status: 'FAILED',
      startTime: isoDaysAgo(10),
      endTime: isoDaysAgo(10, -1),
      createdAt: isoDaysAgo(10),
      updatedAt: isoDaysAgo(10, -1),
      createdBy: user(1),
      updatedBy: user(1),
      isActive: true,
      isDeleted: false,
      evaluationEnvironment: 'SANDBOX',
      historicalEnvironment: 'SANDBOX',
      selectedConversationCount: '600',
      recommendationCount: 0,
      channel: 'web',
    },
    {
      id: 6,
      botId: 'mock-bot',
      botReferenceId: 'mock-bot-ref',
      runId: 'run-cancelled-1',
      runName: 'Manual spot check',
      env: 'SANDBOX',
      status: 'CANCELLED',
      startTime: isoDaysAgo(14),
      endTime: isoDaysAgo(14, -1),
      createdAt: isoDaysAgo(14),
      updatedAt: isoDaysAgo(14, -1),
      createdBy: user(2),
      updatedBy: user(2),
      isActive: true,
      isDeleted: false,
      evaluationEnvironment: 'SANDBOX',
      historicalEnvironment: 'SANDBOX',
      selectedConversationCount: '120',
      recommendationCount: 0,
      channel: 'web',
    },
  ];
}

function seedMetrics(): CalibrationMetric[] {
  return [
    {
      id: 'metric-1',
      displayName: 'Goal Completion',
      metricKey: 'goal_completion',
      type: 'PREDEFINED',
      category: 'quality_metrics',
      usedInRuns: 12,
      createdBy: user(0),
      updatedBy: user(0),
      createdAt: isoDaysAgo(60),
      updatedAt: isoDaysAgo(60),
    },
    {
      id: 'metric-2',
      displayName: 'Tone Consistency',
      metricKey: 'tone_consistency',
      type: 'PREDEFINED',
      category: 'quality_metrics',
      usedInRuns: 9,
      createdBy: user(0),
      updatedBy: user(0),
      createdAt: isoDaysAgo(60),
      updatedAt: isoDaysAgo(60),
    },
    {
      id: 'metric-3',
      displayName: 'Escalation Accuracy',
      metricKey: null,
      type: 'CUSTOM',
      category: 'quality_metrics',
      description: 'Flags conversations that should have escalated to a human agent but did not.',
      usedInRuns: 4,
      createdBy: user(1),
      updatedBy: user(2),
      createdAt: isoDaysAgo(20),
      updatedAt: isoDaysAgo(2),
    },
    {
      id: 'metric-4',
      displayName: 'PII Redaction',
      metricKey: 'pii_redaction',
      type: 'PREDEFINED',
      category: 'safety_metrics',
      usedInRuns: 15,
      createdBy: user(0),
      updatedBy: user(0),
      createdAt: isoDaysAgo(90),
      updatedAt: isoDaysAgo(90),
    },
  ];
}

function seedRunDetails(): Map<string, RunDetail> {
  const map = new Map<string, RunDetail>();
  map.set('run-completed-1', {
    runId: 'run-completed-1',
    runName: 'Weekly quality sweep',
    status: 'COMPLETED',
    createdBy: user(0),
    createdAt: isoDaysAgo(3),
    evaluationEnvironment: 'SANDBOX',
    historicalEnvironment: 'SANDBOX',
    startTime: isoDaysAgo(3),
    endTime: isoDaysAgo(3, -1),
    selectedConversationCount: 482,
    overallEval: {
      categorySummaries: [
        {
          category: 'quality_metrics',
          categoryLabel: 'Quality',
          band: 'medium',
          summary: 'Most conversations resolved the user goal with a consistent tone.',
        },
        {
          category: 'safety_metrics',
          categoryLabel: 'Safety',
          band: 'high',
          summary: 'No PII redaction failures detected in this sample.',
        },
      ],
    },
    metricSummaries: [
      {
        metricId: 'goal_completion',
        metricName: 'Goal Completion',
        category: 'quality_metrics',
        band: 'medium',
        recoCount: 2,
        conversationsAnalyzed: 482,
        conversationsWithIssues: 41,
        summary: 'Goal completion dipped on multi-turn refund requests.',
      },
      {
        metricId: 'pii_redaction',
        metricName: 'PII Redaction',
        category: 'safety_metrics',
        band: 'high',
        recoCount: 1,
        conversationsAnalyzed: 482,
        conversationsWithIssues: 3,
        summary: 'A small number of conversations retained unredacted phone numbers.',
      },
    ],
    items: [
      {
        issueId: 'issue-1',
        title: 'Refund flow loses context after escalation offer',
        issueType: 'goal_completion',
        impactBand: 'medium',
        conversationCount: 41,
        issueDescription:
          'When a user declines the escalation offer, the bot restarts the refund flow instead of resuming it.',
        fix: 'Persist refund-flow state across the escalation-offer branch.',
        contributingMetrics: ['goal_completion'],
      },
      {
        issueId: 'issue-2',
        title: 'Phone numbers occasionally left unredacted in transcripts',
        issueType: 'pii_redaction',
        impactBand: 'high',
        conversationCount: 3,
        issueDescription: 'Redaction misses numbers formatted with a leading country code.',
        fix: 'Extend the redaction pattern to match +<country code> prefixes.',
        contributingMetrics: ['pii_redaction'],
      },
    ],
  });
  return map;
}

function seedEnvironments(): BotEnvironment[] {
  return [
    {
      botRefId: 'mock-env-qa',
      label: 'QA',
      releaseEnv: 'QA',
      envOrder: 0,
      manifestId: 'mock-manifest-qa',
      manifestBuild: 'build_v1',
    },
    {
      botRefId: 'mock-env-uat',
      label: 'UAT',
      releaseEnv: 'UAT',
      envOrder: 1,
      manifestId: 'mock-manifest-uat',
      manifestBuild: 'build_v2',
    },
    {
      botRefId: 'mock-env-live',
      label: 'Production',
      releaseEnv: 'LIVE',
      envOrder: 2,
      manifestId: 'mock-manifest-live',
      manifestBuild: 'build_v2',
    },
  ];
}

let runs = seedRuns();
let metrics = seedMetrics();
const runDetails = seedRunDetails();
const environments = seedEnvironments();
// Ticks an IN_PROGRESS mock run has been polled — see `advanceMockPipeline`.
let inProgressPollTicks = 0;

export const mockStore = {
  listRuns(): CalibrationRun[] {
    return runs;
  },

  getRunDetail(runId: string): RunDetail | undefined {
    return runDetails.get(runId);
  },

  createRun(payload: Record<string, unknown>, context: { env: string }): CalibrationRun {
    nextRunSeq += 1;
    const runId = `run-mock-${nextRunSeq}`;
    const now = new Date().toISOString();
    const created: CalibrationRun = {
      id: nextRunSeq,
      botId: 'mock-bot',
      botReferenceId: 'mock-bot-ref',
      runId,
      runName: typeof payload.runName === 'string' ? payload.runName : `Mock run ${nextRunSeq}`,
      env: context.env,
      status: 'INITIATED',
      createdAt: now,
      updatedAt: now,
      createdBy: user(0),
      updatedBy: user(0),
      isActive: true,
      isDeleted: false,
      evaluationEnvironment: context.env,
      historicalEnvironment: context.env,
      recommendationCount: 0,
    };
    runs = [created, ...runs];
    return created;
  },

  deleteRun(runId: string): void {
    runs = runs.filter((r) => r.runId !== runId);
  },

  cancelRun(runId: string): void {
    runs = runs.map((r) => (r.runId === runId ? { ...r, status: 'CANCELLED' } : r));
  },

  /**
   * Moves the mock pipeline forward by one event-status poll tick, so the
   * bot's single run slot is never stuck:
   *
   * - An IN_PROGRESS run completes after a couple of ticks (long enough to be
   *   visible as "in progress" for at least one poll), freeing the slot.
   * - Otherwise the newest pre-run run (`createRun` prepends, so `find` picks
   *   the one just created, not the oldest) advances to IN_PROGRESS.
   *
   * Without this, nothing in mock mode ever ends a run: an INITIATED run
   * would hold the slot forever, and once advanced, so would the IN_PROGRESS
   * run it became — blocking Create Run indefinitely. Called from the
   * event-status poll, the one thing that ticks while a run is active.
   */
  advanceMockPipeline(): void {
    const active = runs.find((r) => r.status === 'IN_PROGRESS');
    if (active) {
      inProgressPollTicks += 1;
      if (inProgressPollTicks < 2) return;
      inProgressPollTicks = 0;
      runs = runs.map((r) => (r.runId === active.runId ? { ...r, status: 'COMPLETED' } : r));
      return;
    }
    const next = runs.find((r) => r.status === 'INITIATED' || r.status === 'SCHEDULED');
    if (!next) return;
    runs = runs.map((r) => (r.runId === next.runId ? { ...r, status: 'IN_PROGRESS' } : r));
  },

  listMetrics(): CalibrationMetric[] {
    return metrics;
  },

  createMetric(payload: Record<string, unknown>): CalibrationMetric {
    nextMetricSeq += 1;
    const now = new Date().toISOString();
    const created: CalibrationMetric = {
      id: `metric-mock-${nextMetricSeq}`,
      displayName: typeof payload.displayName === 'string' ? payload.displayName : 'Mock metric',
      metricKey: null,
      type: 'CUSTOM',
      category: typeof payload.category === 'string' ? payload.category : 'quality_metrics',
      description: typeof payload.description === 'string' ? payload.description : undefined,
      metadata: (payload.metadata as Record<string, unknown>) ?? {},
      usedInRuns: 0,
      createdBy: user(0),
      updatedBy: user(0),
      createdAt: now,
      updatedAt: now,
    };
    metrics = [created, ...metrics];
    return created;
  },

  updateMetric(id: string, payload: Record<string, unknown>): CalibrationMetric {
    let updated: CalibrationMetric | undefined;
    metrics = metrics.map((m) => {
      if (m.id !== id) return m;
      updated = { ...m, ...payload, updatedAt: new Date().toISOString(), updatedBy: user(1) };
      return updated;
    });
    if (!updated) throw new Error(`Mock metric not found: ${id}`);
    return updated;
  },

  deleteMetric(id: string): void {
    metrics = metrics.filter((m) => m.id !== id);
  },

  listEnvironments(): BotEnvironment[] {
    return environments;
  },

  users(): AISUser[] {
    return MOCK_USERS;
  },
};
